import React, { useMemo } from 'react';
import { Skeleton, Tooltip, Badge } from 'antd';
import { isEmpty } from 'lodash';
import { InfoCircleOutlined } from '@ant-design/icons';
import Table from '@components/common/Table';
import { getMetricShortName } from '@src/utils/metrics';
import { AlertTitle, StyledCard } from './styled';
import useCauseAlerts from './hooks/useCauseAlerts';
import { formatTimeInfo } from './hooks/utils';
import useSetAIAlertAllEventsExport from './hooks/useSetAIAlertAllEventsExport';
import { isAppModule } from '@src/utils/constants';

const getColumns = () => {
  return [
    {
      title: 'Alert Id',
      dataKey: 'alertId',
      key: 'alertId',
      width: 200,
      resizable: true,
      sortable: true,
    },
    {
      title: 'Metric',
      dataKey: 'metricDisplayName',
      key: 'metricDisplayName',
      width: 100,
      dataGetter: ({ rowData }) => {
        const shortName = getMetricShortName({
          metricKey: rowData.metricName,
          hasSubscript: false,
        });
        return shortName;
      },
      resizable: true,
      sortable: true,
    },
    {
      title: 'Peak Severity',
      dataKey: 'peakSeverity',
      key: 'peakSeverity',
      width: 200,
      resizable: true,
      sortable: true,
    },
    // only for video/ad show impacted uniques, for ECO impacted count needs to be introduced
    ...(!isAppModule
      ? [
          {
            title: 'CIUD',
            dataKey: 'cumulativeImpactedUniquesDisplay',
            key: 'cumulativeImpactedUniquesDisplay',
            width: 100,
            headerRenderer: () => (
              <Tooltip title="Cumulative Impacted Unique Devices">
                {'CIUD'}
                <InfoCircleOutlined style={{ fontSize: 12, verticalAlign: 'super' }} />
              </Tooltip>
            ),
            resizable: true,
            sortable: true,
          },
        ]
      : []),
    {
      title: 'Time Alert Fired',
      dataKey: 'eventTime',
      key: 'eventTime',
      width: 200,
      sortable: true,
    },
  ];
};

function AlertTable({ alert, activeAlertId = null }) {
  const { isFetching, alerts, interval, sortBy, onColumnSort }: any = useCauseAlerts({ alert });

  const { columns = [] }: any = useMemo(() => {
    return { columns: getColumns() };
  }, []);

  // Due to cache, the newly occurred alter may not be reflected in the alert table in time.
  const isAlertInList: boolean = useMemo(() => {
    if (alerts) {
      return alerts.some((item) => item?.id === alert?.id);
    }
    return false;
  }, [alerts, alert]);

  useSetAIAlertAllEventsExport({ columns, alerts, alert });

  if (isFetching || isEmpty(alert)) {
    return (
      <StyledCard variant="borderless">
        <Skeleton />
      </StyledCard>
    );
  }

  const addActiveClass = ({ rowData }) => (rowData?.id === activeAlertId ? 'active-event' : '');

  return (
    <StyledCard
      title={
        <AlertTitle>
          Last 30d{' '}
          <Tooltip
            title={`${formatTimeInfo(interval)}. ${!isAlertInList ? 'Current alert will be added to the list after 10 mins.' : ''}`}
          >
            <InfoCircleOutlined />
          </Tooltip>{' '}
          : <i>{alert.rootCauseDisplay}</i>
        </AlertTitle>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Table
        style={{ minHeight: 240, width: '100%', display: 'flex', padding: 10 }}
        // fixed={true}
        data={alerts}
        columns={columns}
        headerHeight={50}
        rowHeight={45}
        rowKey="alertId"
        rowClassName={addActiveClass}
        sortBy={sortBy}
        onColumnSort={onColumnSort}
      />
    </StyledCard>
  );
}

export default AlertTable;
